/**
 * IMGLIB-1 · 归类三件套的选择器：地点（地点清单里启用中的）+ 景点（只列这个地点的）或固定类别（城市风光 / 车辆 / 团队）。
 * 上传弹窗和「改归类」弹窗共用；值编码：景点 = `att:<id>`，固定类别 = `cat:<city|vehicle|team>`。
 */
import { useQuery } from "@tanstack/react-query";
import { listAttractions } from "@/services/attractions";
import { CATEGORY_LABEL, listPlaces, MEDIA_QK, type MediaCategory } from "@/services/mediaLibrary";

export interface Subject {
  placeId: string;
  /** `att:<id>` / `cat:<category>` / "" */
  subject: string;
}

export const subjectToIds = (s: Subject): { place_id: string; attraction_id: string | null; category: MediaCategory | null } => ({
  place_id: s.placeId,
  attraction_id: s.subject.startsWith("att:") ? s.subject.slice(4) : null,
  category: s.subject.startsWith("cat:") ? (s.subject.slice(4) as MediaCategory) : null,
});

export const subjectReady = (s: Subject): boolean => !!s.placeId && !!s.subject;

const sel = "rounded-md border border-slate-300 bg-white px-2.5 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500";

export function SubjectPicker({ value, onChange }: { value: Subject; onChange: (v: Subject) => void }) {
  const placesQ = useQuery({ queryKey: MEDIA_QK.places, queryFn: listPlaces });
  const attractionsQ = useQuery({
    queryKey: ["attractions", "by-place", value.placeId],
    enabled: !!value.placeId,
    queryFn: () => listAttractions({ place_id: value.placeId, status: "active", limit: 200 }),
  });
  const places = (placesQ.data ?? []).filter((p) => p.status === "active");
  const attractions = attractionsQ.data ?? [];
  return (
    <div className="flex flex-col gap-3">
      <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">地点 *
        <select aria-label="地点" className={sel} value={value.placeId} onChange={(e) => onChange({ placeId: e.target.value, subject: "" })}>
          <option value="">请选择地点</option>
          {places.map((p) => <option key={p.id} value={p.id}>{p.name_cn}</option>)}
        </select>
        <span className="text-[11px] font-normal text-slate-400">从「系统设置 → 地点清单」选，不手打</span>
      </label>
      <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">景点 *
        <select aria-label="景点" className={sel} value={value.subject} disabled={!value.placeId} onChange={(e) => onChange({ ...value, subject: e.target.value })}>
          <option value="">{value.placeId ? "请选择景点或固定类别" : "先选地点"}</option>
          {attractions.length > 0 && (
            <optgroup label="景点">
              {attractions.map((a) => <option key={a.id} value={`att:${a.id}`}>{a.name_cn}</option>)}
            </optgroup>
          )}
          <optgroup label="固定类别（不是某个景点的图）">
            {(Object.keys(CATEGORY_LABEL) as MediaCategory[]).map((c) => <option key={c} value={`cat:${c}`}>{CATEGORY_LABEL[c]}</option>)}
          </optgroup>
        </select>
        <span className="text-[11px] font-normal text-slate-400">只列这个地点的景点；不是某个景点的图选固定类别：城市风光 / 车辆 / 团队</span>
      </label>
    </div>
  );
}
